import { useState, useEffect } from 'react'
import { DataGrid, type DataGridColumn } from '@/components/ui/DataGrid'
import { Card, CardContent } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { SearchInput } from '@/components/ui/Input'
import { Badge } from '@/components/ui/Badge'
import {
  usePriceTables,
  usePriceTable,
  useCreatePriceTable,
  useUpdatePriceTable,
  useDeletePriceTable,
  useAddPriceTableItem,
  useUpdatePriceTableItem,
  useDeletePriceTableItem,
  useClonePriceTable,
  useImportPriceTableCSV,
} from '@/hooks/usePriceTables'
import { useCatalogItems } from '@/hooks/useCatalog'
import api from '@/lib/api'
import { formatDate } from '@/lib/utils'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import type {
  PriceTable,
  PriceTableCreate,
  PriceTableUpdate,
  PriceTableItem,
  PriceTableItemCreate,
  PriceTableItemUpdate,
  PriceTableStatus,
  CatalogItem,
} from '@/types'
import {
  Plus, Edit2, Trash2, X, AlertTriangle, Copy, Eye, Loader2, DollarSign, Upload, Download, FileSpreadsheet, CheckCircle2,
} from 'lucide-react'

// ==========================================
// Constants
// ==========================================

const STATUS_CONFIG: Record<PriceTableStatus, { label: string; variant: 'default' | 'secondary' | 'destructive' | 'success' | 'outline' }> = {
  draft: { label: 'Rascunho', variant: 'secondary' },
  active: { label: 'Ativa', variant: 'success' },
  expired: { label: 'Expirada', variant: 'destructive' },
  archived: { label: 'Arquivada', variant: 'outline' },
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value)
}

function getApiErrorMessage(err: unknown, fallback: string): string {
  const axiosErr = err as { response?: { data?: { detail?: unknown } } }
  const detail = axiosErr?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail)) return detail.map((d: Record<string, string>) => d.msg).join('; ')
  return fallback
}

const selectClass = 'flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm'

// ==========================================
// Main Page
// ==========================================

export function PriceTablesPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [filterStatus, setFilterStatus] = useState<PriceTableStatus | ''>('')
  const [filterSegment, setFilterSegment] = useState('')

  const [showCreate, setShowCreate] = useState(false)
  const [editTableId, setEditTableId] = useState<string | null>(null)
  const [viewTableId, setViewTableId] = useState<string | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState<PriceTable | null>(null)

  const cloneMutation = useClonePriceTable()
  const [showImportCSV, setShowImportCSV] = useState(false)

  const { data, isLoading } = usePriceTables({
    page,
    per_page: 20,
    search: search || undefined,
    status: filterStatus || undefined,
    segment: filterSegment || undefined,
  })

  const tables = data?.items ?? []
  const totalPages = data?.pages ?? 1

  // Collect unique segments for filter
  const segments = [...new Set(tables.map((t) => t.segment).filter(Boolean) as string[])]

  function handleClone(table: PriceTable) {
    cloneMutation.mutate(table.id)
  }

  const columns: DataGridColumn<PriceTable>[] = [
    {
      id: 'code',
      header: 'Codigo',
      accessor: (r) => <span className="font-mono text-xs">{r.code}</span>,
      sortValue: (r) => r.code,
      filterable: true,
      filterValue: (r) => r.code,
    },
    {
      id: 'name',
      header: 'Nome',
      accessor: (r) => <span className="font-medium">{r.name}</span>,
      sortValue: (r) => r.name,
      filterable: true,
      filterValue: (r) => r.name,
    },
    {
      id: 'status',
      header: 'Status',
      accessor: (r) => {
        const cfg = STATUS_CONFIG[r.status]
        return <Badge variant={cfg.variant}>{cfg.label}</Badge>
      },
      sortValue: (r) => r.status,
    },
    {
      id: 'validity',
      header: 'Vigencia',
      accessor: (r) => (
        <span className="text-sm">
          {formatDate(r.valid_from)} - {formatDate(r.valid_until)}
        </span>
      ),
      sortValue: (r) => r.valid_from,
    },
    {
      id: 'segment',
      header: 'Segmento',
      accessor: (r) => r.segment ?? '-',
      sortValue: (r) => r.segment ?? '',
    },
    {
      id: 'region',
      header: 'Regiao',
      accessor: (r) => r.region ?? '-',
    },
    {
      id: 'currency',
      header: 'Moeda',
      accessor: (r) => r.currency,
    },
    {
      id: 'items_count',
      header: 'Itens',
      accessor: (r) => (
        <Badge variant="outline">{r.items_count ?? r.items?.length ?? 0}</Badge>
      ),
      sortValue: (r) => r.items_count ?? r.items?.length ?? 0,
      align: 'center',
    },
    {
      id: 'actions',
      header: 'Acoes',
      accessor: (r) => (
        <div className="flex gap-1">
          <Button variant="ghost" size="icon" title="Ver" onClick={(e) => { e.stopPropagation(); setViewTableId(r.id) }}>
            <Eye className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" title="Editar" onClick={(e) => { e.stopPropagation(); setEditTableId(r.id) }}>
            <Edit2 className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" title="Clonar" onClick={(e) => { e.stopPropagation(); handleClone(r) }}>
            <Copy className="h-4 w-4" />
          </Button>
          <Button variant="ghost" size="icon" title="Excluir" onClick={(e) => { e.stopPropagation(); setDeleteConfirm(r) }}>
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ),
      sortable: false,
    },
  ]

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Tabelas de Preco</h1>
          <p className="text-muted-foreground">Gerencie tabelas de preco e itens</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowImportCSV(true)}>
            <Upload className="h-4 w-4 mr-2" />
            Importar CSV
          </Button>
          <Button onClick={() => setShowCreate(true)}>
            <Plus className="h-4 w-4 mr-2" />
            Nova Tabela
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <SearchInput
                placeholder="Buscar por codigo ou nome..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1) }}
              />
            </div>
            <select
              className={selectClass + ' w-[160px]'}
              value={filterStatus}
              onChange={(e) => { setFilterStatus(e.target.value as PriceTableStatus | ''); setPage(1) }}
            >
              <option value="">Todos os status</option>
              <option value="draft">Rascunho</option>
              <option value="active">Ativa</option>
              <option value="expired">Expirada</option>
              <option value="archived">Arquivada</option>
            </select>
            {segments.length > 0 && (
              <select
                className={selectClass + ' w-[160px]'}
                value={filterSegment}
                onChange={(e) => { setFilterSegment(e.target.value); setPage(1) }}
              >
                <option value="">Todos segmentos</option>
                {segments.map((s) => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>
            )}
          </div>
        </CardContent>
      </Card>

      {/* DataGrid */}
      <Card>
        <CardContent className="p-0">
          <DataGrid
            gridId="price-tables"
            columns={columns}
            data={tables}
            isLoading={isLoading}
            emptyMessage="Nenhuma tabela de preco encontrada"
            emptyIcon={<DollarSign className="h-12 w-12 text-muted-foreground" />}
          />
        </CardContent>
      </Card>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)}>
            Anterior
          </Button>
          <span className="text-sm text-muted-foreground">
            Pagina {page} de {totalPages} ({data?.total ?? 0} tabelas)
          </span>
          <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>
            Proxima
          </Button>
        </div>
      )}

      {/* Modals */}
      {showCreate && <PriceTableFormPanel onClose={() => setShowCreate(false)} />}
      {editTableId && <PriceTableFormPanel tableId={editTableId} onClose={() => setEditTableId(null)} />}
      {viewTableId && <PriceTableViewPanel tableId={viewTableId} onClose={() => setViewTableId(null)} onEdit={() => { setEditTableId(viewTableId); setViewTableId(null) }} />}
      {deleteConfirm && <DeleteTableModal table={deleteConfirm} onClose={() => setDeleteConfirm(null)} />}
      {showImportCSV && <ImportCSVModal onClose={() => setShowImportCSV(false)} />}
    </div>
  )
}

// ==========================================
// View Panel (Slide-over, read-only)
// ==========================================

function PriceTableViewPanel({
  tableId,
  onClose,
  onEdit,
}: {
  tableId: string
  onClose: () => void
  onEdit: () => void
}) {
  const { data: table, isLoading } = usePriceTable(tableId)

  if (isLoading || !table) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
        <div className="bg-card w-full max-w-2xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  const cfg = STATUS_CONFIG[table.status]

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="sticky top-0 z-10 bg-card border-b px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <h2 className="text-lg font-semibold">{table.name}</h2>
            <Badge variant={cfg.variant}>{cfg.label}</Badge>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={onEdit}>
              <Edit2 className="h-4 w-4 mr-1" /> Editar
            </Button>
            <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
          </div>
        </div>

        <div className="p-6 space-y-6">
          {/* Info */}
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div><span className="text-muted-foreground">Codigo:</span> <span className="font-mono">{table.code}</span></div>
            <div><span className="text-muted-foreground">Moeda:</span> {table.currency}</div>
            <div><span className="text-muted-foreground">Vigencia:</span> {formatDate(table.valid_from)} - {formatDate(table.valid_until)}</div>
            <div><span className="text-muted-foreground">Segmento:</span> {table.segment ?? '-'}</div>
            <div><span className="text-muted-foreground">Regiao:</span> {table.region ?? '-'}</div>
            {table.description && (
              <div className="col-span-2"><span className="text-muted-foreground">Descricao:</span> {table.description}</div>
            )}
          </div>

          {/* Items */}
          <div>
            <h3 className="font-semibold mb-3">Itens ({table.items?.length ?? 0})</h3>
            {table.items && table.items.length > 0 ? (
              <div className="border rounded-md overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="px-3 py-2 text-left">Produto</th>
                      <th className="px-3 py-2 text-left">Codigo</th>
                      <th className="px-3 py-2 text-right">Preco Unit.</th>
                      <th className="px-3 py-2 text-right">Qtd Min</th>
                      <th className="px-3 py-2 text-right">Desc. Max (%)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {table.items.map((item) => (
                      <tr key={item.id} className="border-b last:border-0">
                        <td className="px-3 py-2">{item.catalog_item_name ?? '-'}</td>
                        <td className="px-3 py-2 font-mono text-xs">{item.catalog_item_code ?? '-'}</td>
                        <td className="px-3 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                        <td className="px-3 py-2 text-right">{item.min_quantity}</td>
                        <td className="px-3 py-2 text-right">{item.max_discount_percent}%</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-muted-foreground text-sm">Nenhum item adicionado</p>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ==========================================
// Form Panel (Slide-over)
// ==========================================

function PriceTableFormPanel({
  tableId,
  onClose,
}: {
  tableId?: string
  onClose: () => void
}) {
  const isEdit = !!tableId
  const { data: existingTable, isLoading: loadingTable } = usePriceTable(tableId ?? null)

  const createMutation = useCreatePriceTable()
  const updateMutation = useUpdatePriceTable()
  const addItemMutation = useAddPriceTableItem()
  const updateItemMutation = useUpdatePriceTableItem()
  const deleteItemMutation = useDeletePriceTableItem()

  const { data: catalogData } = useCatalogItems({ per_page: 100, is_active: true })
  const catalogItems: CatalogItem[] = catalogData?.items ?? []

  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [status, setStatus] = useState<PriceTableStatus>('draft')
  const [validFrom, setValidFrom] = useState('')
  const [validUntil, setValidUntil] = useState('')
  const [segment, setSegment] = useState('')
  const [region, setRegion] = useState('')
  const [currency, setCurrency] = useState('BRL')
  const [error, setError] = useState('')
  const [confirmDeleteItemId, setConfirmDeleteItemId] = useState<string | null>(null)

  // For new item inline form
  const [showAddItem, setShowAddItem] = useState(false)
  const [newItemCatalogId, setNewItemCatalogId] = useState('')
  const [newItemPrice, setNewItemPrice] = useState('')
  const [newItemMinQty, setNewItemMinQty] = useState('1')
  const [newItemMaxDiscount, setNewItemMaxDiscount] = useState('0')

  // For editing existing item
  const [editingItemId, setEditingItemId] = useState<string | null>(null)
  const [editItemPrice, setEditItemPrice] = useState('')
  const [editItemMinQty, setEditItemMinQty] = useState('')
  const [editItemMaxDiscount, setEditItemMaxDiscount] = useState('')

  // Load existing table data
  useEffect(() => {
    if (existingTable) {
      setCode(existingTable.code)
      setName(existingTable.name)
      setDescription(existingTable.description ?? '')
      setStatus(existingTable.status)
      setValidFrom(existingTable.valid_from?.split('T')[0] ?? '')
      setValidUntil(existingTable.valid_until?.split('T')[0] ?? '')
      setSegment(existingTable.segment ?? '')
      setRegion(existingTable.region ?? '')
      setCurrency(existingTable.currency)
    }
  }, [existingTable])

  const saving = createMutation.isPending || updateMutation.isPending

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError('')

    if (!code.trim() || !name.trim() || !validFrom || !validUntil) {
      setError('Codigo, Nome e Vigencia sao obrigatorios')
      return
    }

    try {
      if (isEdit && tableId) {
        const payload: PriceTableUpdate = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          status,
          valid_from: validFrom,
          valid_until: validUntil,
          segment: segment.trim() || null,
          region: region.trim() || null,
          currency: currency.trim() || 'BRL',
        }
        await updateMutation.mutateAsync({ id: tableId, data: payload })
        onClose()
      } else {
        const payload: PriceTableCreate = {
          code: code.trim(),
          name: name.trim(),
          description: description.trim() || null,
          status,
          valid_from: validFrom,
          valid_until: validUntil,
          segment: segment.trim() || null,
          region: region.trim() || null,
          currency: currency.trim() || 'BRL',
        }
        await createMutation.mutateAsync(payload)
        onClose()
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao salvar tabela'))
    }
  }

  async function handleAddItem() {
    if (!tableId || !newItemCatalogId || !newItemPrice) return
    setError('')
    try {
      const payload: PriceTableItemCreate = {
        catalog_item_id: newItemCatalogId,
        unit_price: parseFloat(newItemPrice),
        min_quantity: parseInt(newItemMinQty) || 1,
        max_discount_percent: parseFloat(newItemMaxDiscount) || 0,
      }
      await addItemMutation.mutateAsync({ tableId, item: payload })
      setShowAddItem(false)
      setNewItemCatalogId('')
      setNewItemPrice('')
      setNewItemMinQty('1')
      setNewItemMaxDiscount('0')
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao adicionar item'))
    }
  }

  function startEditItem(item: PriceTableItem) {
    setEditingItemId(item.id)
    setEditItemPrice(String(item.unit_price))
    setEditItemMinQty(String(item.min_quantity))
    setEditItemMaxDiscount(String(item.max_discount_percent))
  }

  async function handleSaveItem() {
    if (!editingItemId) return
    setError('')
    try {
      const payload: PriceTableItemUpdate = {
        unit_price: parseFloat(editItemPrice),
        min_quantity: parseInt(editItemMinQty) || 1,
        max_discount_percent: parseFloat(editItemMaxDiscount) || 0,
      }
      await updateItemMutation.mutateAsync({ id: editingItemId, data: payload })
      setEditingItemId(null)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao atualizar item'))
    }
  }

  async function handleDeleteItem(itemId: string) {
    setError('')
    try {
      await deleteItemMutation.mutateAsync(itemId)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao remover item'))
    }
  }

  if (isEdit && loadingTable) {
    return (
      <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
        <div className="bg-card w-full max-w-2xl h-full flex items-center justify-center" onClick={(e) => e.stopPropagation()}>
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      </div>
    )
  }

  const tableItems: PriceTableItem[] = existingTable?.items ?? []

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/50" onClick={onClose}>
      <div className="bg-card w-full max-w-2xl h-full overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="sticky top-0 z-10 bg-card border-b px-6 py-4 flex items-center justify-between">
          <h2 className="text-lg font-semibold">{isEdit ? 'Editar Tabela de Preco' : 'Nova Tabela de Preco'}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}><X className="h-5 w-5" /></Button>
        </div>

        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          {/* Table Info Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium">Codigo *</label>
                <input className={selectClass + ' mt-1'} value={code} onChange={(e) => setCode(e.target.value)} placeholder="Ex: TAB-2026-01" />
              </div>
              <div>
                <label className="text-sm font-medium">Nome *</label>
                <input className={selectClass + ' mt-1'} value={name} onChange={(e) => setName(e.target.value)} placeholder="Nome da tabela" />
              </div>
            </div>

            <div>
              <label className="text-sm font-medium">Descricao</label>
              <textarea
                className={selectClass + ' mt-1 min-h-[60px]'}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Descricao opcional"
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Status</label>
                <select className={selectClass + ' mt-1'} value={status} onChange={(e) => setStatus(e.target.value as PriceTableStatus)}>
                  <option value="draft">Rascunho</option>
                  <option value="active">Ativa</option>
                  <option value="expired">Expirada</option>
                  <option value="archived">Arquivada</option>
                </select>
              </div>
              <div>
                <label className="text-sm font-medium">Vigencia De *</label>
                <input type="date" className={selectClass + ' mt-1'} value={validFrom} onChange={(e) => setValidFrom(e.target.value)} />
              </div>
              <div>
                <label className="text-sm font-medium">Vigencia Ate *</label>
                <input type="date" className={selectClass + ' mt-1'} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium">Segmento</label>
                <input className={selectClass + ' mt-1'} value={segment} onChange={(e) => setSegment(e.target.value)} placeholder="Ex: Enterprise" />
              </div>
              <div>
                <label className="text-sm font-medium">Regiao</label>
                <input className={selectClass + ' mt-1'} value={region} onChange={(e) => setRegion(e.target.value)} placeholder="Ex: Brasil" />
              </div>
              <div>
                <label className="text-sm font-medium">Moeda</label>
                <input className={selectClass + ' mt-1'} value={currency} onChange={(e) => setCurrency(e.target.value)} placeholder="BRL" />
              </div>
            </div>

            <div className="flex gap-3 pt-2">
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
                {isEdit ? 'Salvar Tabela' : 'Criar Tabela'}
              </Button>
              <Button type="button" variant="outline" onClick={onClose}>Cancelar</Button>
            </div>
          </form>

          {/* Items Section (edit mode only) */}
          {isEdit && tableId && (
            <div className="border-t pt-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold">Itens da Tabela ({tableItems.length})</h3>
                <Button variant="outline" size="sm" onClick={() => setShowAddItem(true)}>
                  <Plus className="h-4 w-4 mr-1" /> Adicionar Item
                </Button>
              </div>

              {/* Add item inline form */}
              {showAddItem && (
                <div className="border rounded-md p-4 mb-4 bg-muted/30 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Produto *</label>
                      <select className={selectClass + ' mt-1'} value={newItemCatalogId} onChange={(e) => setNewItemCatalogId(e.target.value)}>
                        <option value="">Selecione um produto...</option>
                        {catalogItems.map((ci) => (
                          <option key={ci.id} value={ci.id}>{ci.code} - {ci.name}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="text-sm font-medium">Preco Unitario (R$) *</label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        className={selectClass + ' mt-1'}
                        value={newItemPrice}
                        onChange={(e) => setNewItemPrice(e.target.value)}
                        placeholder="0.00"
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-sm font-medium">Qtd Minima</label>
                      <input
                        type="number"
                        min="1"
                        className={selectClass + ' mt-1'}
                        value={newItemMinQty}
                        onChange={(e) => setNewItemMinQty(e.target.value)}
                      />
                    </div>
                    <div>
                      <label className="text-sm font-medium">Desconto Max (%)</label>
                      <input
                        type="number"
                        min="0"
                        max="100"
                        step="0.1"
                        className={selectClass + ' mt-1'}
                        value={newItemMaxDiscount}
                        onChange={(e) => setNewItemMaxDiscount(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" onClick={handleAddItem} disabled={addItemMutation.isPending || !newItemCatalogId || !newItemPrice}>
                      {addItemMutation.isPending && <Loader2 className="h-3 w-3 mr-1 animate-spin" />}
                      Adicionar
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => setShowAddItem(false)}>Cancelar</Button>
                  </div>
                </div>
              )}

              {/* Items table */}
              {tableItems.length > 0 ? (
                <div className="border rounded-md overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-muted/50">
                        <th className="px-3 py-2 text-left">Produto</th>
                        <th className="px-3 py-2 text-right">Preco Unit.</th>
                        <th className="px-3 py-2 text-right">Qtd Min</th>
                        <th className="px-3 py-2 text-right">Desc. Max (%)</th>
                        <th className="px-3 py-2 text-center">Acoes</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tableItems.map((item) => (
                        <tr key={item.id} className="border-b last:border-0">
                          {editingItemId === item.id ? (
                            <>
                              <td className="px-3 py-2">{item.catalog_item_name ?? item.catalog_item_code ?? '-'}</td>
                              <td className="px-3 py-2">
                                <input type="number" step="0.01" min="0" className={selectClass + ' text-right h-8 text-sm'} value={editItemPrice} onChange={(e) => setEditItemPrice(e.target.value)} />
                              </td>
                              <td className="px-3 py-2">
                                <input type="number" min="1" className={selectClass + ' text-right h-8 text-sm'} value={editItemMinQty} onChange={(e) => setEditItemMinQty(e.target.value)} />
                              </td>
                              <td className="px-3 py-2">
                                <input type="number" min="0" max="100" step="0.1" className={selectClass + ' text-right h-8 text-sm'} value={editItemMaxDiscount} onChange={(e) => setEditItemMaxDiscount(e.target.value)} />
                              </td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={handleSaveItem} disabled={updateItemMutation.isPending}>
                                    {updateItemMutation.isPending ? <Loader2 className="h-3 w-3 animate-spin" /> : <span className="text-xs font-bold text-green-500">OK</span>}
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setEditingItemId(null)}>
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          ) : (
                            <>
                              <td className="px-3 py-2">{item.catalog_item_name ?? item.catalog_item_code ?? '-'}</td>
                              <td className="px-3 py-2 text-right">{formatCurrency(item.unit_price)}</td>
                              <td className="px-3 py-2 text-right">{item.min_quantity}</td>
                              <td className="px-3 py-2 text-right">{item.max_discount_percent}%</td>
                              <td className="px-3 py-2 text-center">
                                <div className="flex gap-1 justify-center">
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => startEditItem(item)}>
                                    <Edit2 className="h-3 w-3" />
                                  </Button>
                                  <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setConfirmDeleteItemId(item.id)} disabled={deleteItemMutation.isPending}>
                                    <Trash2 className="h-3 w-3 text-destructive" />
                                  </Button>
                                </div>
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm text-center py-6">Nenhum item adicionado. Clique em "Adicionar Item" para comecar.</p>
              )}
            </div>
          )}
        </div>
      </div>

      <ConfirmDialog
        open={!!confirmDeleteItemId}
        title="Excluir item da tabela?"
        message="Este item será removido da tabela de preços. Esta ação não pode ser desfeita."
        confirmLabel="Excluir"
        variant="destructive"
        isLoading={deleteItemMutation.isPending}
        onConfirm={async () => {
          if (!confirmDeleteItemId) return
          await handleDeleteItem(confirmDeleteItemId)
          setConfirmDeleteItemId(null)
        }}
        onCancel={() => setConfirmDeleteItemId(null)}
      />
    </div>
  )
}

// ==========================================
// Delete Modal
// ==========================================

function DeleteTableModal({ table, onClose }: { table: PriceTable; onClose: () => void }) {
  const mutation = useDeletePriceTable()
  const [error, setError] = useState('')

  async function handleDelete() {
    setError('')
    try {
      await mutation.mutateAsync(table.id)
      onClose()
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao excluir tabela'))
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={(e) => { if (e.target === e.currentTarget) onClose() }}>
      <div className="bg-card rounded-lg shadow-2xl p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 rounded-full bg-destructive/10">
            <AlertTriangle className="h-5 w-5 text-destructive" />
          </div>
          <h3 className="text-lg font-semibold">Excluir Tabela de Preco</h3>
        </div>
        <p className="text-muted-foreground mb-1">
          Tem certeza que deseja excluir a tabela <strong>{table.name}</strong> ({table.code})?
        </p>
        <p className="text-sm text-muted-foreground mb-4">
          Todos os itens da tabela tambem serao removidos.
        </p>
        {error && (
          <div className="p-3 rounded-md bg-destructive/10 text-destructive text-sm mb-4">{error}</div>
        )}
        <div className="flex gap-3 justify-end">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button variant="destructive" onClick={handleDelete} disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Excluir
          </Button>
        </div>
      </div>
    </div>
  )
}

export default PriceTablesPage


// ==========================================
// Import CSV Modal
// ==========================================

function ImportCSVModal({ onClose }: { onClose: () => void }) {
  const [file, setFile] = useState<File | null>(null)
  const [dragOver, setDragOver] = useState(false)
  const [downloading, setDownloading] = useState(false)
  const importMutation = useImportPriceTableCSV()

  async function handleDownloadTemplate() {
    setDownloading(true)
    try {
      const response = await api.get('/v1/price-tables/import-csv/template', {
        responseType: 'blob',
      })
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', 'template_tabela_precos_nai.csv')
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(url)
    } catch {
      alert('Erro ao baixar template')
    } finally {
      setDownloading(false)
    }
  }

  function handleDrop(e: React.DragEvent) {
    e.preventDefault()
    setDragOver(false)
    const f = e.dataTransfer.files[0]
    if (f && (f.name.endsWith('.csv') || f.type === 'text/csv')) {
      setFile(f)
    }
  }

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    if (f) setFile(f)
  }

  function handleImport() {
    if (!file) return
    importMutation.mutate(file)
  }

  const result = importMutation.data

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={onClose}>
      <div
        className="bg-card rounded-lg shadow-2xl w-full max-w-lg mx-4"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div className="flex items-center gap-2">
            <FileSpreadsheet className="h-5 w-5 text-primary" />
            <h2 className="text-lg font-semibold">Importar Tabela de Precos via CSV</h2>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-5 w-5" />
          </Button>
        </div>

        <div className="p-6 space-y-5">
          {/* Step 1: Download template */}
          <div>
            <h3 className="text-sm font-medium mb-2">1. Baixe o template CSV</h3>
            <p className="text-xs text-muted-foreground mb-3">
              O template inclui todos os itens do catalogo. Preencha os dados da tabela no cabecalho e os precos de cada item.
            </p>
            <Button variant="outline" size="sm" onClick={handleDownloadTemplate} disabled={downloading}>
              {downloading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Download className="h-4 w-4 mr-2" />}
              Baixar Template
            </Button>
          </div>

          {/* Step 2: Upload filled CSV */}
          {!result && (
            <div>
              <h3 className="text-sm font-medium mb-2">2. Envie o CSV preenchido</h3>
              <div
                className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-muted-foreground/25 hover:border-primary/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true) }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => document.getElementById('csv-file-input')?.click()}
              >
                <input
                  id="csv-file-input"
                  type="file"
                  accept=".csv"
                  className="hidden"
                  onChange={handleFileSelect}
                />
                {file ? (
                  <div className="flex items-center justify-center gap-2">
                    <FileSpreadsheet className="h-6 w-6 text-primary" />
                    <span className="text-sm font-medium">{file.name}</span>
                    <span className="text-xs text-muted-foreground">({(file.size / 1024).toFixed(1)} KB)</span>
                  </div>
                ) : (
                  <div>
                    <Upload className="h-8 w-8 mx-auto text-muted-foreground mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Arraste o arquivo CSV aqui ou clique para selecionar
                    </p>
                  </div>
                )}
              </div>

              {importMutation.isError && (
                <div className="mt-3 p-3 bg-destructive/10 border border-destructive/20 rounded-md">
                  <div className="flex items-start gap-2">
                    <AlertTriangle className="h-4 w-4 text-destructive mt-0.5 shrink-0" />
                    <p className="text-sm text-destructive">
                      {getApiErrorMessage(importMutation.error, 'Erro ao importar CSV')}
                    </p>
                  </div>
                </div>
              )}

              <div className="mt-4 flex justify-end gap-2">
                <Button variant="outline" onClick={onClose}>Cancelar</Button>
                <Button
                  onClick={handleImport}
                  disabled={!file || importMutation.isPending}
                >
                  {importMutation.isPending ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Upload className="h-4 w-4 mr-2" />
                  )}
                  Importar
                </Button>
              </div>
            </div>
          )}

          {/* Step 3: Result */}
          {result && (
            <div className="space-y-3">
              <div className="flex items-center gap-2 text-green-600 dark:text-green-400">
                <CheckCircle2 className="h-5 w-5" />
                <h3 className="text-sm font-medium">Importacao concluida!</h3>
              </div>

              <div className="bg-muted/50 rounded-md p-4 space-y-2 text-sm">
                <div className="grid grid-cols-2 gap-2">
                  <span className="text-muted-foreground">Tabela:</span>
                  <span className="font-medium">{result.table_name}</span>
                  <span className="text-muted-foreground">Codigo:</span>
                  <span className="font-mono">{result.table_code}</span>
                  <span className="text-muted-foreground">Vigencia:</span>
                  <span>{result.valid_from} a {result.valid_until}</span>
                  <span className="text-muted-foreground">Itens criados:</span>
                  <span className="font-medium text-green-600 dark:text-green-400">{result.items_created}</span>
                  <span className="text-muted-foreground">Itens ignorados:</span>
                  <span>{result.items_skipped}</span>
                </div>
              </div>

              {result.errors && result.errors.length > 0 && (
                <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md p-3">
                  <p className="text-xs font-medium text-yellow-800 dark:text-yellow-200 mb-1">
                    Avisos ({result.errors.length}):
                  </p>
                  <ul className="text-xs text-yellow-700 dark:text-yellow-300 space-y-0.5 max-h-32 overflow-y-auto">
                    {result.errors.map((err: string, i: number) => (
                      <li key={i}>• {err}</li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="flex justify-end">
                <Button onClick={onClose}>Fechar</Button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
